import React, { useMemo, useState } from 'react';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { Paper, useMediaQuery } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { OrderData, OrderStatus, PassStatus, WaitingListItemData } from '../api';
import { PassCard } from './components/PassCard';
import { BookingCard, BookingEntry, UpcomingEntry, UpcomingEntryType, WaitlistEntry } from './components/BookingCard';
import { ExpirationBanner } from './components/ExpirationBanner';
import { EmptyState } from './components/EmptyState';
import { BookNewSessionFab } from './components/BookNewSessionFab';
import { CancellationSheet, CancellationType } from './components/CancellationSheet';
import { QrCheckinSheet } from './components/QrCheckinSheet';
import { getExperienceName, getPassOrderPage, isPassCurrent } from './utils/passUtils';
import { getPassCancelHoursBefore, isPassCancelAllowed } from './utils/passCancellationUtils';
import { useManagePasses } from './ManagePassesContext';
import { useLanguage } from '../utils/GeneralUtils';
import { getDateFormat } from '../utils/getDateAndTimeFormat';
import { AccountLogo } from '../shared/ui/AccountLogo';
import { LanguageSwitcher } from '../shared/ui/LanguageSwitcher';
import { InstallPrompt } from './pwa/InstallPrompt';
import RotatingLoader from '../ui/RotatingLoader';

export const PassDashboard = () => {
  const { t } = useTranslation();
  const language = useLanguage();
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const { passes, orders, account, isLoading, logout, cancelOrder, waitlistItems, cancelWaitlistItem } =
    useManagePasses();
  const { longDateFormat } = getDateFormat(account);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState<OrderData | null>(null);
  const [cancelWaitlistDialogOpen, setCancelWaitlistDialogOpen] = useState(false);
  const [waitlistItemToCancel, setWaitlistItemToCancel] = useState<WaitingListItemData | null>(null);
  const [qrOrder, setQrOrder] = useState<OrderData | null>(null);

  const upcomingWaitlistItems = useMemo(
    () =>
      waitlistItems
        .filter((item) => item.date && moment(item.date).isAfter(moment()))
        .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf()),
    [waitlistItems],
  );

  const upcomingBookings = useMemo(
    () =>
      orders
        .filter(
          (o) => o.date && moment(o.date).isAfter(moment()) && o.status !== OrderStatus.ARCHIVED && !o.member_order_id,
        )
        .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf()),
    [orders],
  );

  const upcomingEntries = useMemo(() => {
    const entries: UpcomingEntry[] = [
      ...upcomingBookings.map((o): BookingEntry => ({ type: UpcomingEntryType.BOOKING, data: o })),
      ...upcomingWaitlistItems.map((w): WaitlistEntry => ({ type: UpcomingEntryType.WAITLIST, data: w })),
    ];
    entries.sort((a, b) => moment(a.data.date).valueOf() - moment(b.data.date).valueOf());
    return entries;
  }, [upcomingBookings, upcomingWaitlistItems]);

  // Rendering nothing here used to leave the customer on a blank page whenever the lookup came back
  // without an account. Always show something they can read.
  if (!account) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '60dvh' }}>
        {isLoading ? <RotatingLoader /> : <EmptyState message={t('manage_passes.login.notFound')} />}
      </Grid>
    );
  }

  const visiblePasses = passes.filter((p) => p.status !== PassStatus.PENDING_PAYMENT);
  const activePasses = visiblePasses.filter((p) => isPassCurrent(p, getPassOrderPage(account, p.order_page_id)));
  const inactivePasses = visiblePasses.filter((p) => !isPassCurrent(p, getPassOrderPage(account, p.order_page_id)));

  const handleCancelClick = (order: OrderData) => {
    setOrderToCancel(order);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async (confirmed: boolean) => {
    if (confirmed && orderToCancel) {
      await cancelOrder(orderToCancel.id);
    }
    setCancelDialogOpen(false);
    setOrderToCancel(null);
  };

  const handleCancelWaitlistClick = (item: WaitingListItemData) => {
    setWaitlistItemToCancel(item);
    setCancelWaitlistDialogOpen(true);
  };

  const handleCancelWaitlistConfirm = async (confirmed: boolean) => {
    if (confirmed && waitlistItemToCancel) {
      await cancelWaitlistItem(waitlistItemToCancel.id);
    }
    setCancelWaitlistDialogOpen(false);
    setWaitlistItemToCancel(null);
  };

  return (
    <Grid container flexDirection="column" gap={isSmallScreen ? 2.5 : 3} pb={10}>
      <Grid container alignItems="center" justifyContent="space-between" sx={{ pt: 1 }}>
        <Grid container alignItems="center" gap={1.5} sx={{ flex: 1 }}>
          <AccountLogo
            account={account}
            width={80}
            style={{ width: 80, borderRadius: 8, maxHeight: 80, objectFit: 'contain' }}
          />
          <Typography variant="h6" fontWeight={600}>
            {account.label?.[language] || account.name}
          </Typography>
        </Grid>
        <LanguageSwitcher languages={account.customer_facing_widgets_languages || []} />
      </Grid>

      <ExpirationBanner passes={visiblePasses} account={account} />

      {passes[0] && (
        <Grid container flexDirection="column" gap={0.5}>
          <Typography variant="body1" fontWeight={600}>
            {passes[0].firstname} {passes[0].lastname}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {passes[0].email}
          </Typography>
        </Grid>
      )}

      <Grid container flexDirection="column" gap={isSmallScreen ? 1.5 : 2}>
        <Typography variant="h5" fontWeight={600}>
          {t('manage_passes.dashboard.upcomingBookings')}
        </Typography>
        {upcomingEntries.length > 0 ? (
          upcomingEntries.map((entry) => (
            <BookingCard
              key={entry.data.id}
              entry={entry}
              account={account}
              onClick={
                account.qr_checkin_enabled && entry.type === UpcomingEntryType.BOOKING
                  ? () => setQrOrder(entry.data)
                  : undefined
              }
              onCancel={
                entry.type === UpcomingEntryType.WAITLIST
                  ? () => handleCancelWaitlistClick(entry.data)
                  : entry.data.pass_id
                  ? () => handleCancelClick(entry.data)
                  : undefined
              }
            />
          ))
        ) : (
          <EmptyState message={t('manage_passes.dashboard.noUpcomingBookings')} />
        )}
      </Grid>

      <Grid container flexDirection="column" gap={isSmallScreen ? 1.5 : 2}>
        <Grid container alignItems="center" justifyContent="space-between">
          <Typography variant="h5" fontWeight={600}>
            {t('manage_passes.dashboard.yourPasses')}
          </Typography>
          <Button variant="outlined" size="small" onClick={() => navigate('/passes/buy')} sx={{ borderRadius: 2 }}>
            {t('manage_passes.dashboard.buyPass')}
          </Button>
        </Grid>
        {activePasses.length > 0 ? (
          activePasses.map((pass) => <PassCard key={pass.id} pass={pass} />)
        ) : (
          <Paper
            elevation={0}
            onClick={() => navigate('/passes/buy')}
            sx={{
              borderRadius: 2,
              p: 2,
              cursor: 'pointer',
              border: '1px dashed',
              borderColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}>
            <Typography color="text.secondary">{t('manage_passes.dashboard.noPassesBanner')}</Typography>
            <Grid container alignItems="center" gap={0.5} sx={{ width: 'auto', flexShrink: 0 }}>
              <Typography color="primary" fontWeight={600} variant="body2">
                {t('manage_passes.dashboard.noPassesBannerCta')}
              </Typography>
              <ArrowForwardIcon color="primary" fontSize="small" />
            </Grid>
          </Paper>
        )}
      </Grid>

      {inactivePasses.length > 0 && (
        <Grid container flexDirection="column" gap={isSmallScreen ? 1.5 : 2}>
          <Typography variant="h6" color="text.secondary">
            {t('manage_passes.dashboard.pastPasses')}
          </Typography>
          {inactivePasses.map((pass) => (
            <PassCard key={pass.id} pass={pass} />
          ))}
        </Grid>
      )}

      <Grid container justifyContent="center">
        <Button variant="text" size="small" color="inherit" onClick={logout} sx={{ color: 'text.secondary' }}>
          {t('manage_passes.dashboard.logout')}
        </Button>
      </Grid>

      <BookNewSessionFab />

      <InstallPrompt />

      <CancellationSheet
        isOpen={cancelDialogOpen}
        onClose={handleCancelConfirm}
        experienceName={orderToCancel ? getExperienceName(account, orderToCancel.order_page_id, language) : ''}
        bookingDate={orderToCancel?.date ? moment(orderToCancel.date).format(longDateFormat) : ''}
        cancelHoursBefore={orderToCancel ? getPassCancelHoursBefore(account, orderToCancel.order_page_id) : undefined}
        cancelAllowed={
          !orderToCancel ||
          isPassCancelAllowed(
            getPassCancelHoursBefore(account, orderToCancel.order_page_id),
            orderToCancel.date || '',
            orderToCancel.time,
          )
        }
      />

      <CancellationSheet
        isOpen={cancelWaitlistDialogOpen}
        onClose={handleCancelWaitlistConfirm}
        experienceName={
          waitlistItemToCancel ? getExperienceName(account, waitlistItemToCancel.order_page_id, language) : ''
        }
        bookingDate={waitlistItemToCancel?.date ? moment(waitlistItemToCancel.date).format(longDateFormat) : ''}
        cancellationType={CancellationType.WAITLIST}
      />

      <QrCheckinSheet
        isOpen={!!qrOrder}
        onClose={() => setQrOrder(null)}
        qrUrl={qrOrder?.qr_url}
        experienceName={qrOrder ? getExperienceName(account, qrOrder.order_page_id, language) : ''}
        bookingDate={qrOrder?.date ? moment(qrOrder.date).format(longDateFormat) : ''}
      />
    </Grid>
  );
};
